import { LayoutMulti, LayoutSingle } from '@/hooks/layouts/defaultState';
import {
    BPP_TO_CODES,
    COLOR_MAP,
    DisplayOption,
    FullLayoutItem,
    LayoutId,
    LayoutList,
    MultiFormateRelationMap,
    MultiRelationsMap,
    RESOLUTION_TO_SIZE,
    SingleFormateRelationMap,
    SingleRelationsMap,
} from '@/types/pages/layout';
import deepEqual from 'fast-deep-equal';
import { get } from 'lodash';

export function getStyleInfo(
    options: DisplayOption[],
    displayType: string,
    styleId?: number | null,
): { id: number | null; slug: string | null } {
    const opt = options.find((o) => o.value === displayType);
    const id = styleId || opt?.multiLayoutOptions[0]?.id || null;
    const style = opt?.multiLayoutOptions?.find((s) => s.id === id);
    return {
        slug: style?.slug ?? null,
        id: style?.id ?? null,
    };
}
export function getStyleSlug(options: DisplayOption[], displayType: string, styleId: number | null): string | null {
    const opt = options.find((o) => o.value === displayType);
    const style = opt?.multiLayoutOptions?.find((s) => s.id === styleId);
    return style?.slug ?? null;
}

export function getStyleIds(rel: MultiFormateRelationMap, displayType: string, slug: string | null): LayoutList[] {
    if (!slug) return [];
    return rel[displayType]?.[slug]?.list ?? [];
}

export function getAssociatedLayouts(
    rel: MultiFormateRelationMap,
    displayType: string,
    slug: string | null,
): LayoutList[] {
    return getStyleIds(rel, displayType, slug);
}

export function setStyleIds(
    rel: MultiFormateRelationMap,
    displayType: string,
    slug: string,
    ids: LayoutList[],
): MultiFormateRelationMap {
    const prev = rel[displayType][slug];
    const exists = ids.some((item) => item.uuid === prev.default_layout);
    return {
        ...rel,
        [displayType]: {
            ...rel[displayType],
            [slug]: {
                list: ids,
                default_layout: exists ? prev.default_layout : ids?.[0]?.uuid || null,
            },
        },
    };
}

export function detachIds(
    rel: MultiFormateRelationMap,
    displayType: string,
    slug: string,
    idsToRemove: string[],
): MultiFormateRelationMap {
    const current = getStyleIds(rel, displayType, slug);
    const next = current.filter((o) => !idsToRemove.includes(o.uuid));
    return setStyleIds(rel, displayType, slug, next);
}

export function attachIds(
    rel: MultiFormateRelationMap,
    displayType: string,
    slug: string,
    idsToAdd: LayoutList[],
): MultiFormateRelationMap {
    const current = getStyleIds(rel, displayType, slug);
    const next = [...current, ...idsToAdd];
    return setStyleIds(rel, displayType, slug, next);
}

export function buildLayoutsById(subLayouts: FullLayoutItem[]) {
    return Object.fromEntries(
        subLayouts.map((l) => [
            Number(l.id),
            {
                ...l,
                images: l.images.map((url) => `${url}?t=${Date.now()}`),
            },
        ]),
    );
}

export function parseSizeAndColorsFromXml(xml: string) {
    try {
        const doc = new DOMParser().parseFromString(xml, 'application/xml');

        const panel = doc.getElementsByTagName('PANEL')?.[0];

        if (!panel) return { size: '', colors: [], bpp: null, resolution: '' };

        const bppStr = panel.getAttribute('bpp');
        const wStr = panel.getAttribute('width');
        const hStr = panel.getAttribute('height');

        const bpp = bppStr && /^\d+$/.test(bppStr) ? bppStr : '0';
        const width = wStr && /^\d+$/.test(wStr) ? wStr : null;
        const height = hStr && /^\d+$/.test(hStr) ? hStr : null;

        const resolution = width != null && height != null ? `${width}x${height}` : '';
        const size = RESOLUTION_TO_SIZE[resolution] ?? '';
        const codes = BPP_TO_CODES[bpp] || [];

        const colors = codes.map((c: string) => COLOR_MAP[c]?.hex).filter(Boolean) as string[];

        return { size, colors, bpp: Number(bpp), resolution };
    } catch {
        return { size: '', colors: [], bpp: null, resolution: '' };
    }
}

export function removeRelationsById(rel: MultiFormateRelationMap, id: string) {
    const newRel: MultiFormateRelationMap = {};
    for (const [display, styleInfo] of Object.entries(rel)) {
        for (const [slug, relationInfo] of Object.entries(styleInfo)) {
            const list = relationInfo.list.filter((o) => o.id !== id);
            const defaultId = relationInfo.default_layout;
            const exists = list.some((item) => item.uuid === defaultId);
            newRel[display] = {
                ...newRel[display],
                [slug]: {
                    list,
                    default_layout: exists ? defaultId : list?.[0]?.uuid || null,
                },
            };
        }
    }
    return newRel;
}

export function mergeListAndConfig(relations: MultiRelationsMap, layoutsById: Record<LayoutId, FullLayoutItem>) {
    const formatRelationsMap: MultiFormateRelationMap = {};

    // key
    Object.entries(relations).forEach(([baseKey, styles]) => {
        if (baseKey.endsWith('_conf')) return;
        formatRelationsMap[baseKey] = {};
        Object.entries(styles).forEach(([slug, ids]) => {
            formatRelationsMap[baseKey][slug] = {
                list: (ids as number[])
                    .filter((id) => layoutsById[id])
                    .map((id) => ({
                        id: String(id),
                        name: layoutsById[id].name,
                        uuid: `multi-${id}-${crypto.randomUUID()}`,
                    })),
                default_layout: null,
            };
        });
    });

    // conf
    Object.entries(relations).forEach(([baseKey, styles]) => {
        if (baseKey.endsWith('_conf')) {
            const key = baseKey.replace(/_conf$/, '');
            Object.entries(styles).forEach(([slug, slugInfo]) => {
                const defaultLayout = get(slugInfo, 'default_layout') || 0;
                const init = defaultLayout > 0 ? defaultLayout : 0;
                formatRelationsMap[key][slug].default_layout = formatRelationsMap[key][slug].list?.[init]?.uuid ?? null;
            });
        }
    });

    // last check default
    Object.entries(formatRelationsMap).forEach(([baseKey, styles]) => {
        Object.entries(styles).forEach(([slug, slugInfo]) => {
            const { list, default_layout } = slugInfo;
            formatRelationsMap[baseKey][slug].default_layout = default_layout
                ? default_layout
                : list.length > 0
                  ? list[0].uuid
                  : null;
        });
    });

    return formatRelationsMap;
}
export function clearRelations(relations: MultiFormateRelationMap) {
    const result: MultiFormateRelationMap = {};

    for (const [outerKey, outerVal] of Object.entries(relations)) {
        result[outerKey] = {};
        for (const [innerKey, innerVal] of Object.entries(outerVal)) {
            result[outerKey][innerKey] = {
                ...innerVal,
                list: [],
            };
        }
    }

    return result;
}

// SINGLE
export function mergeListAndConfigForSingle(
    relations: SingleRelationsMap,
    layoutsById: Record<LayoutId, FullLayoutItem>,
) {
    const formatRelationsMap: SingleFormateRelationMap = {};

    // key
    Object.entries(relations).forEach(([baseKey, list]) => {
        if (baseKey.endsWith('_conf')) return;
        formatRelationsMap[baseKey] = {
            list: (list as number[])
                .filter((id) => layoutsById[id])
                .map((id) => ({
                    id: String(id),
                    name: layoutsById[id].name,
                    uuid: `single-${id}-${crypto.randomUUID()}`,
                })),
            default_layout: null,
        };
    });

    // conf
    Object.entries(relations).forEach(([baseKey, o]) => {
        if (baseKey.endsWith('_conf')) {
            const key = baseKey.replace(/_conf$/, '');
            const defaultLayout = get(o, 'default_layout') || 0;
            const init = defaultLayout > 0 ? defaultLayout : 0;
            formatRelationsMap[key].default_layout = formatRelationsMap[key].list?.[init]?.uuid ?? null;
        }
    });

    // last check default
    Object.keys(formatRelationsMap).forEach((baseKey) => {
        if (!formatRelationsMap[baseKey].default_layout) {
            formatRelationsMap[baseKey].default_layout = formatRelationsMap[baseKey].list[0]?.uuid || null;
        }
    });

    return formatRelationsMap;
}
export function setSingleStyleIds(
    rel: SingleFormateRelationMap,
    displayType: string,
    ids: LayoutList[],
): SingleFormateRelationMap {
    const prev = rel[displayType];
    const exists = ids.some((item) => item.uuid === prev.default_layout);
    return {
        ...rel,
        [displayType]: {
            list: ids,
            default_layout: exists ? prev.default_layout : ids?.[0]?.uuid || null,
        },
    };
}
export function detachSingleIds(
    rel: SingleFormateRelationMap,
    displayType: string,
    idsToRemove: string[],
): SingleFormateRelationMap {
    const current = getSingleIds(rel, displayType);
    const next = current.filter((o) => !idsToRemove.includes(o.uuid));
    return setSingleStyleIds(rel, displayType, next);
}

export function removeSingleRelationsById(rel: SingleFormateRelationMap, id: string) {
    const newRel: SingleFormateRelationMap = {};
    for (const [display, relationInfo] of Object.entries(rel)) {
        const list = relationInfo.list.filter((o) => o.id !== id);
        const defaultId = relationInfo.default_layout;
        const exists = list.some((item) => item.uuid === defaultId);
        newRel[display] = {
            list,
            default_layout: exists ? defaultId : list?.[0]?.uuid || null,
        };
    }
    return newRel;
}

export function getSingleIds(rel: SingleFormateRelationMap, displayType: string): LayoutList[] {
    return rel[displayType]?.list ?? [];
}

export function clearSingleRelations(relations: SingleFormateRelationMap) {
    const result: SingleFormateRelationMap = {};

    for (const [outerKey] of Object.entries(relations)) {
        result[outerKey] = {
            default_layout: null,
            list: [],
        };
    }
    return result;
}

export function getIsLayoutChange(single: LayoutSingle, multi: LayoutMulti) {
    const isSingleChange =
        deepEqual(single.initialRelations, single.relations) &&
        deepEqual(single.initialLayoutsById, single.layoutsById);
    const isMultiChange =
        deepEqual(multi.initialRelations, multi.relations) && deepEqual(multi.initialLayoutsById, multi.layoutsById);
    return !isSingleChange || !isMultiChange;
}

function cleanXml(text: string): string {
    return text.replace(/\\u([0-9A-Fa-f]{4})/g, (_match, hex) => String.fromCharCode(parseInt(hex, 16)));
}

function xmlToBase64Sync(xmlContent: string): string {
    const bytes = new TextEncoder().encode(xmlContent);
    let binary = '';
    const chunkSize = 0x8000; // 32KB，避免 apply 引數過長

    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        // Array.from 可避免 TS 對 apply 參數型別抱怨
        binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    return btoa(binary);
}

export function xmlEscapedToBase64(xml: string): string {
    const cleaned = cleanXml(xml);
    return xmlToBase64Sync(cleaned);
}
